import * as React from 'react'
import { Slot } from '@radix-ui/react-slot'
import { cva, type VariantProps } from 'class-variance-authority'

import { cn } from '@/lib/utils'
import { Search } from 'lucide-react'
import { useTranslation } from 'react-i18next'

const buttonVariants = cva(
  "inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-md text-sm font-medium transition-all active:scale-95 disabled:pointer-events-none disabled:opacity-40 [&_svg]:pointer-events-none [&_svg:not([class*='size-'])]:size-4 shrink-0 [&_svg]:shrink-0 outline-none focus-visible:border-ring focus-visible:ring-ring/50 focus-visible:ring-[3px] aria-invalid:ring-destructive/20 dark:aria-invalid:ring-destructive/40 aria-invalid:border-destructive cursor-pointer",
  {
    variants: {
      variant: {
        default: 'text-primary bg-primary text-white',
        destructive: 'bg-[#FEECEB] text-destructive hover:bg-[#FEECEB]/80',
        outline:
          'border bg-background shadow-xs hover:bg-accent hover:text-accent-foreground dark:bg-input/30 dark:border-input dark:hover:bg-input/50',
        secondary: 'bg-secondary text-primary hover:bg-primary/20',
        border: 'border border-primary hover:bg-primary/20 text-primary',
        ghost: 'hover:bg-accent hover:text-accent-foreground dark:hover:bg-accent/50',
        link: 'text-primary underline-offset-4 hover:underline',
        icon: 'bg-secondary text-primary hover:bg-secondary/80'
      },
      size: {
        default: 'h-9 px-4 py-2 has-[>svg]:px-3',
        sm: 'h-8 rounded-md gap-1.5 px-3 has-[>svg]:px-2.5',
        lg: 'h-10 rounded-md px-6 has-[>svg]:px-4',
        icon: 'size-8',
        'icon-sm': 'size-8',
        'icon-lg': 'size-10'
      }
    },
    defaultVariants: {
      variant: 'default',
      size: 'default'
    }
  }
)

function Button({
  className,
  variant,
  size,
  asChild = false,
  ...props
}: React.ComponentProps<'button'> &
  VariantProps<typeof buttonVariants> & {
    asChild?: boolean
  }) {
  const Comp = asChild ? Slot : 'button'

  return (
    <Comp
      data-slot='button'
      className={cn(buttonVariants({ variant, size, className }))}
      type='button'
      {...props}
    />
  )
}

function ButtonSearch({ ...props }: React.ComponentProps<'button'>) {
  const { t } = useTranslation()
  return (
    <Button
      type='submit'
      variant='outline'
      {...props}
      className='xl:w-36 self-end text-primary border-primary hover:bg-secondary'
    >
      <Search size={16} />
      {t('Search' as any)}
    </Button>
  )
}

function ButtonIcon({
  children,
  size = 'icon',
  className,
  variant = 'icon',
  ...props
}: React.ComponentProps<'button'> & VariantProps<typeof buttonVariants>) {
  return (
    <div className='p-1'>
      <Button
        type='button'
        variant={variant}
        size={size}
        className={cn('p-2', className)}
        {...props}
      >
        {children}
      </Button>
    </div>
  )
}

export { Button, ButtonSearch, ButtonIcon, buttonVariants }
